import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';
import iconXpCardStudent from '../../../assets/student/Icon-xp-card-student.png';
import { useTheme } from '../../theme';
import Button from '../Button';

export type VideoProgressCardStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed';

export interface VideoProgressCardProps extends HTMLAttributes<HTMLDivElement> {
  coverImageUrl: string;
  coverImageAlt: string;
  xp: number;
  progressPercent: number;
  showProgress?: boolean;
  /** Quando `false`, não mostra o badge de XP na capa (ex.: secção Watch again). */
  showXp?: boolean;
  leftTopBadgeLabel?: string;
  title: string;
  description: string;
  status: VideoProgressCardStatus;
  onActionClick: () => void;
  actionDisabled?: boolean;
  actionIconSrc?: string;
  actionIconAlt?: string;
  actionLabelOverride?: string;
  /** Data de conclusão (ISO); mostra "Completed on" + data abaixo da descrição. */
  completedAt?: string | null;
  /** Botão com fundo branco, texto e borda no verde institucional — ex.: secção Watch again. */
  actionButtonVariant?: 'default' | 'outline_green';
}

function getActionLabelKey(status: VideoProgressCardStatus): string {
  switch (status) {
    case 'not_started':
      return 'video_progress_card.actions.start';
    case 'in_progress':
      return 'video_progress_card.actions.continue';
    case 'completed':
      return 'video_progress_card.actions.reload';
    default:
      return 'video_progress_card.actions.continue';
  }
}

function clampProgress(progressPercent: number): number {
  if (Number.isNaN(progressPercent)) {
    return 0;
  }

  if (progressPercent < 0) {
    return 0;
  }

  if (progressPercent > 100) {
    return 100;
  }

  return Math.round(progressPercent);
}

/** Data no formato `yyyy-mm-dd` (calendário local). */
function formatCompletedDateYmd(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function VideoProgressCard({
  coverImageUrl,
  coverImageAlt,
  xp,
  progressPercent,
  showProgress = true,
  showXp = true,
  leftTopBadgeLabel,
  title,
  description,
  status,
  onActionClick,
  actionDisabled = false,
  actionIconSrc,
  actionIconAlt = '',
  actionLabelOverride,
  completedAt,
  actionButtonVariant = 'default',
  style,
  ...rest
}: VideoProgressCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [hasImageError, setHasImageError] = useState(false);

  const normalizedProgress = clampProgress(progressPercent);
  const shouldShowImage = Boolean(coverImageUrl) && !hasImageError;
  const actionLabel = actionLabelOverride ?? t(getActionLabelKey(status));

  let completedOnFormatted: string | null = null;
  if (completedAt != null && completedAt !== '') {
    completedOnFormatted = formatCompletedDateYmd(completedAt);
  }

  const isOutlineGreen = actionButtonVariant === 'outline_green';
  const systemGreen = theme.palette.text.primary;

  return (
    <article
      style={{
        boxSizing: 'border-box',
        width: 441,
        minHeight: 444.94,
        backgroundColor: theme.palette.background.paper,
        border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
        borderRadius: theme.shape.borderRadius.lg,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        ...style,
      }}
      {...rest}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          flex: 1,
          minHeight: 0,
          maxHeight: 230.0,
          backgroundColor: theme.palette.background.default,
          overflow: 'hidden',
        }}
      >
        {shouldShowImage ? (
          <img
            src={coverImageUrl}
            alt={coverImageAlt}
            onError={() => setHasImageError(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <div
            aria-label={coverImageAlt}
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'linear-gradient(135deg, rgba(18,38,58,0.9) 0%, rgba(39,62,84,0.92) 100%)',
              color: theme.palette.text.white,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold,
            }}
          >
            Sem capa
          </div>
        )}

        {leftTopBadgeLabel ? (
          <div
            style={{
              position: 'absolute',
              top: theme.spacing.md,
              left: theme.spacing.md,
              boxSizing: 'border-box',
              minHeight: 28,
              display: 'inline-flex',
              alignItems: 'center',
              gap: theme.spacing.xs,
              padding: `${theme.spacing.xs} ${theme.spacing.sm2}`,
              borderRadius: theme.shape.borderRadius.sm2,
              backgroundColor: theme.palette.text.primary,
              color: theme.palette.text.white,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.fontSize.xs,
              fontWeight: theme.typography.fontWeight.medium,
            }}
          >
            {leftTopBadgeLabel}
          </div>
        ) : null}

        {showXp ? (
          <div
            style={{
              position: 'absolute',
              top: theme.spacing.md,
              right: theme.spacing.md,
              boxSizing: 'border-box',
              width: 91.66,
              height: 28,
              minHeight: 28,
              display: 'inline-flex',
              alignItems: 'center',
              gap: theme.spacing.xs,
              padding: `${theme.spacing.xs} ${theme.spacing.sm2}`,
              borderRadius: theme.shape.borderRadius.sm2,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold,
            }}
          >
            <img
              src={iconXpCardStudent}
              alt=""
              width={16}
              height={16}
              style={{ display: 'block', objectFit: 'contain' }}
              aria-hidden
            />
            <span
              style={{
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.semibold,
              }}
            >
              {t('video_progress_card.xp_value', { xp })}
            </span>
          </div>
        ) : null}

        {showProgress ? (
          <div
            style={{
              position: 'absolute',
              left: theme.spacing.md,
              right: theme.spacing.md,
              bottom: theme.spacing.md,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing.xs,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: theme.palette.text.white,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSize.md,
                fontWeight: theme.typography.fontWeight.medium,
              }}
            >
              <span>{t('video_progress_card.progress_label')}</span>
              <span>{normalizedProgress}%</span>
            </div>

            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={normalizedProgress}
              aria-label={t('video_progress_card.progress_aria')}
              style={{
                width: '100%',
                height: 8,
                borderRadius: theme.shape.borderRadius.full,
                backgroundColor: 'rgba(255, 255, 255, 0.45)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${normalizedProgress}%`,
                  height: '100%',
                  backgroundColor: theme.palette.primary.main,
                }}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div
        style={{
          boxSizing: 'border-box',
          minHeight: 196,
          padding: `${theme.spacing.lg} ${theme.spacing.lg} ${
            isOutlineGreen ? theme.spacing.lg : 0
          } ${theme.spacing.lg}`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          gap: theme.spacing.md,
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: theme.spacing.sm,
            overflow: 'hidden',
          }}
        >
          <h3
            style={{
              margin: 0,
              color: theme.palette.text.primary,
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.fontWeight.bold,
              fontSize: theme.typography.fontSize.lg,
              lineHeight: theme.typography.lineHeight.tight,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </h3>

          <p
            style={{
              margin: 0,
              color: theme.palette.text.secondary,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.fontSize.base,
              lineHeight: theme.typography.lineHeight.normal,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {description}
          </p>
        </div>

        {completedOnFormatted != null ? (
          <p
            style={{
              margin: 0,
              flexShrink: 0,
              color: systemGreen,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              lineHeight: theme.typography.lineHeight.normal,
            }}
          >
            {t('video_progress_card.completed_on')}{' '}
            <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>
              {completedOnFormatted}
            </span>
          </p>
        ) : null}

        <Button
          type="button"
          size="large"
          onClick={onActionClick}
          disabled={actionDisabled}
          backgroundColor={isOutlineGreen ? theme.palette.background.paper : undefined}
          color={isOutlineGreen ? systemGreen : undefined}
          style={{
            width: '100%',
            height: 40,
            fontSize: theme.typography.fontSize.base,
            fontWeight: theme.typography.fontWeight.semibold,
            minHeight: 40,
            flexShrink: 0,
            // Mantemos apenas padding horizontal para o conteúdo ficar centralizado.
            padding: `0 ${theme.spacing.buttonLargeX}`,
            ...(isOutlineGreen
              ? {
                  border: `1px solid ${systemGreen}`,
                  boxSizing: 'border-box',
                }
              : {}),
          }}
        >
          {actionIconSrc ? (
            <img
              src={actionIconSrc}
              alt={actionIconAlt}
              width={14}
              height={14}
              style={{ display: 'block', objectFit: 'contain', marginRight: 6 }}
            />
          ) : null}
          {actionLabel}
        </Button>
      </div>
    </article>
  );
}
